
import { dig } from 'vessel/utils'
import { pinchZoom } from './zoom'

const colors = {
  dark: {
    background: 'black',
    text: 'grey-lighter'
  },
  light: {
    background: 'white',
    text: 'grey-darker'
  }
}

export default {
  name: 'VsLightbox',
  model: {
    prop: 'visible',
    event: 'update:visible'
  },
  props: {
    name: {
      type: String,
      default: null
    },
    items: {
      type: Array,
      default: null
    },
    active: {
      type: Object,
      default: null
    },
    background: {
      type: [String, Object],
      default: null
    },
    color: {
      type: [String, Object],
      default: null
    },
    mode: {
      type: String,
      default: 'dark'
    },
    visible: {
      type: Boolean,
      default: false
    },
    zIndex: {
      type: [Number, String],
      default: 9999
    }
  },
  vessel: {
    thumbnail: {
      opacity: '50'
    },
    activeThumbnail: {
      opacity: '100'
    },
    icon: {
      fontSize: '3xl',
      cursor: 'pointer'
    },
    nav: {
      $extend: 'icon',
      position: 'relative',
      zIndex: '10'
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue != oldValue) {
          newValue ? this.open() : this.close()
        }
      }
    },
    active: {
      immediate: true,
      handler(value) {
        if (value) {
          this.activeItem = value
        }
      }
    }
  },
  data() {
    return {
      activeItem: null,
      activated: false,
      showMedia: true,
      mediaEnter: 'right',
      mediaLeave: 'left',
      thumbSize: {
        all: 'thumb',
        lg: 'md',
        xl: 'lg'
      }
    }
  },
  computed: {
    itemData() {
      return this.activeItem || this.items[0] || {}
    },
    itemIndex() {
      return this.items.findIndex(item => item.src == this.itemData.src)
    },
    total() {
      return this.items.length
    },
    multiple() {
      return this.total > 1
    },
    showNav() {
      return this.multiple && (this.$screen.touch ? this.$screen.xl : true)
    },
    dark() {
      return this.mode == 'dark'
    },
    mediaColumns() {
      return this.showNav ? 10 : 12
    },
    textColor() {
      return this.color || dig(colors, `${this.mode}.text`)
    },
    backgroundColor() {
      return this.background || dig(colors, `${this.mode}.background`)
    }
  },
  methods: {
    show(item) {
      this.activeItem = item
      this.open()
    },
    open() {
      this.activated = true

      this.$emit('open')
      this.$emit('update:visible', true)
    },
    close() {
      this.activated = false

      this.$emit('close')
      this.$emit('update:visible', false)
    },
    toggle() {
      this.activated ? this.close() : this.open()
    },
    onEnter() {
      if (this.name) {
        this.$lightbox.active = this.name
      }

      this.$nextTick(() => {
        this.enableZoom()
        this.updateThumbails({ behavior: 'instant' })
      })
    },
    onLeave() {
      if (this.name && this.$lightbox.active == this.name) {
        this.$lightbox.active = null
      }
    },
    onPrev() {
      if (!this.multiple) return

      const item = this.itemIndex > 0
        ? this.items[(this.itemIndex - 1)]
        : this.items[(this.total - 1)]

      this.activate(item)
    },
    onNext() {
      if (!this.multiple) return

      const item = this.itemIndex < this.total - 1
        ? this.items[(this.itemIndex + 1)]
        : this.items[0]

      this.activate(item)
    },
    activate(item) {
      if (this.activeItem === item) return

      const prevIndex = this.items.indexOf(this.activeItem)
      const nextIndex = this.items.indexOf(item)
      const lastIndex = this.total - 1

      const toLast  = prevIndex == 0 && nextIndex == lastIndex
      const toFirst = nextIndex == 0 && prevIndex == lastIndex
      const toPrev  = prevIndex > nextIndex && !toFirst

      if (toPrev || toLast) {
        this.mediaEnter = 'left'
        this.mediaLeave = 'right'
      } else {
        this.mediaEnter = 'right'
        this.mediaLeave = 'left'
      }

      this.showMedia  = false
      this.activeItem = item

      this.updateThumbails()

      setTimeout(() => {
        this.showMedia = true
        this.enableZoom()
      }, 300)
    },
    enableZoom() {
      this.$nextTick(() => {
        pinchZoom(this.$refs.preview.$el)
      })
    },
    thumbnailClass(item) {
      return item.src === this.itemData.src
        ? this.$vessels.activeThumbnail
        : this.$vessels.thumbnail
    },
    updateThumbails(options) {
      if (!this.multiple || !this.$refs.thumbnails)
        return

      const current = this.$refs.thumbnails[this.itemIndex]
      if (!current) return

      let target = current.$el

      const root = target.parentElement
      const prev = target.previousElementSibling
      const next = target.nextElementSibling

      if (prev && next) {
        if (root.scrollLeft > (prev.offsetLeft - root.offsetLeft)) {
          target = prev
        } else {
          target = next
        }
      }

      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
        ...options
      })
    }
  },
  mounted() {
    if (this.name) {
      this.$lightbox.register(this.name, this)
    }
  },
  destroyed() {
    if (this.name) {
      this.$lightbox.unregister(this.name)
    }
  }
}
